import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { writetoLS } from "$utils/utils";
import { DockyardServices, EDBService } from "$services";
import useDockyardForm from "$hooks/useDockyardForm";
import useAuth from "$hooks/useAuth";
import { deleteDockyard } from "$services/Dockyard.service";
import { CacheInvalidationAction } from "$utils/cache";
import { useSnackbar } from "$utils/useSnackbar";
import { snackbarErrorOptions } from "$utils/SnackbarOptions";
import { DockyardDetailsLogic } from "../types";

const DOCKYARD_DEACTIVATION_BLOCKED_MESSAGE =
  "Ce chantier ne peut pas être désactivé car des EDB ou préparations sont en cours.";

const isDockyardDeactivationBlockedError = (message?: string | null) =>
  !!message?.includes("Impossible de désactiver ce chantier");

export default function useDockyardDetailsLogic(): DockyardDetailsLogic {
  const param = useParams<"idChantier">();
  const { idChantier } = param;
  const { isAdmin, isClient, token, isContributor } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openSnackbarError] = useSnackbar(snackbarErrorOptions);

  const [modal, setModal] = useState<boolean>(false);
  const [modalActive, setModalActive] = useState<boolean>(false);
  const [modalDeleteOpen, setModalDeleteOpen] = useState<boolean>(false);
  const [hasOrders, setHasOrders] = useState<boolean>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: dockyard, refetch: refetchDockyard } = useQuery({
    queryKey: [CacheInvalidationAction.GET_DOCKYARD_BY_ID, idChantier],
    queryFn: () => DockyardServices.getDockyardById(idChantier),
    enabled: !!idChantier,
    staleTime: 1000 * 60,
  });

  const { data: edbList, isLoading: isLoadingEdb } = useQuery({
    queryKey: [CacheInvalidationAction.GET_EDBS, { dockyard: idChantier }],
    queryFn: () => EDBService.getEDBs({ dockyard: idChantier }),
    enabled: !!idChantier,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const isDone = dockyard?.status === "done";
  const canEdit = isAdmin();
  const isDockyardDisabled = dockyard?.isDisable || false;
  const canToggleDockyard =
    canEdit && !dockyard?.projectDisable && !dockyard?.companyDisable;
  const isFullyDisabled =
    isDone ||
    isDockyardDisabled ||
    dockyard?.projectDisable ||
    dockyard?.companyDisable ||
    !canEdit;

  const hasEdbLeft = !(edbList?.data || []).every(
    (edb) =>
      edb.status?.includes("collected") || edb.status?.includes("delivered")
  );

  const [formValues, setFormValues] = useState<any>({
    medias: (dockyard?.medias || []).map((media: any) => media || {}),
  });

  const { control, setValue } = useForm({
    mode: "onChange",
  });

  useEffect(() => {
    setFormValues({
      medias: (dockyard?.medias || []).map((media: any) => media || {}),
    });
  }, [dockyard]);

  useEffect(() => {
    setValue("medias", formValues.medias, { shouldValidate: false });
  }, [formValues.medias, setValue, formValues]);

  const disableDockyardMutation = useMutation({
    mutationFn: () =>
      DockyardServices.DisabledDockyard(token, idChantier || ""),
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Une erreur est survenue lors de la désactivation du chantier";
      openSnackbarError(
        isDockyardDeactivationBlockedError(message)
          ? DOCKYARD_DEACTIVATION_BLOCKED_MESSAGE
          : message
      );
      setModalActive(false);
    },
    onSuccess: (result: any) => {
      if (result?.error) {
        openSnackbarError(
          isDockyardDeactivationBlockedError(result.error)
            ? DOCKYARD_DEACTIVATION_BLOCKED_MESSAGE
            : result.error
        );
        setModalActive(false);
        return;
      }
      setModalActive(false);
      refetchDockyard();
      queryClient.invalidateQueries({
        queryKey: [CacheInvalidationAction.GET_EDBS],
      });
      setErrorMessage(null);
    },
  });

  const enableDockyardMutation = useMutation({
    mutationFn: () => DockyardServices.EnableDockyard(token, idChantier || ""),
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Une erreur est survenue lors de l'activation du chantier";
      setErrorMessage(message);
      setModalActive(false);
    },
    onSuccess: () => {
      setModalActive(false);
      refetchDockyard();
      queryClient.invalidateQueries({
        queryKey: [CacheInvalidationAction.GET_EDBS],
      });
      setErrorMessage(null);
    },
  });

  const {
    classes: dockyardFormClasses,
    togglePopin,
    openPopin: openPopinDockyard,
    handleSubmit,
    isOpened,
    submitting,
    error,
    dockyard: formDockyard,
  } = useDockyardForm();

  const handleSwitchToggle = async () => {
    setModalActive(true);
  };

  const DisableDockyard = async () => {
    await disableDockyardMutation.mutateAsync();
  };

  const EnableDockyard = async () => {
    const dkProj: any = dockyard?.project;
    if (
      !dkProj?.isDisable &&
      !dkProj?.companyDisable &&
      dkProj?.status !== "done"
    ) {
      await enableDockyardMutation.mutateAsync();
    }
  };

  const handleCloseError = () => {
    setErrorMessage(null);
  };

  const handleClickChantier = async () => {
    if (dockyard) {
      await openPopinDockyard(dockyard._id || (dockyard as any).id);
    }
  };

  const handleClickTerminate = async () => {
    await DockyardServices.UpdateDockyardStatus(
      token,
      { status: "done" },
      idChantier || ""
    );
    setModal(false);
  };

  const handleDeleteDockyard = async () => {
    if (dockyard?._id) {
      await deleteDockyard(token, dockyard._id);
      navigate("/chantiers");
    }
  };

  const handleEdbRedirect = async () => {
    await writetoLS("dockyard", JSON.stringify(dockyard));
    navigate("/create-edb");
  };

  const filteredInterveningCompanies = dockyard?.interveningCompany?.filter(
    (c) => c.isActive
  );

  return {
    idChantier,
    dockyard,
    isLoadingEdb,
    edbList,
    isDone,
    canEdit,
    isDockyardDisabled,
    canToggleDockyard,
    isFullyDisabled,
    hasEdbLeft,
    hasOrders,
    setHasOrders,
    filteredInterveningCompanies,
    control,
    isAdminUser: isAdmin(),
    isContributorUser: isContributor(),
    isClientUser: isClient(),
    modal,
    setModal,
    modalActive,
    setModalActive,
    modalDeleteOpen,
    setModalDeleteOpen,
    isOpened,
    submitting,
    error,
    formDockyard,
    dockyardFormClasses,
    togglePopin,
    handleClickChantier,
    handleSubmit,
    handleSwitchToggle,
    DisableDockyard,
    EnableDockyard,
    handleClickTerminate,
    handleDeleteDockyard,
    handleEdbRedirect,
    errorMessage,
    handleCloseError,
    isTogglePending:
      disableDockyardMutation.isPending || enableDockyardMutation.isPending,
  };
}
